import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { library as api } from "@/lib/api/library";
import type { BookTree, Locale } from "@/lib/api/library";
import type { Article, Book, Chapter } from "@/types/api";
import { useAuth } from "./useAuth";

// Library is read-only for the reader surface. Authoring is a Phase 7+
// admin/author surface that ships separately. The hook caches the
// books index and the per-book tree so swapping books doesn't refire the
// chapter/article fan-out — bodies are still lazy through fetchArticle.

export type LibraryStatus = "idle" | "loading" | "ready" | "error";

export interface LibraryContextValue {
  status: LibraryStatus;
  books: readonly Book[];
  locale: Locale;
  error: Error | null;
  setLocale: (locale: Locale) => void;
  reload: () => Promise<void>;
  /** One-shot reader payload — book + chapters + article metadata. Cached
   *  per (locale, slug); same call returns the same object on a hit. */
  fetchTree: (bookIdOrSlug: string) => Promise<BookTree>;
  invalidateTree: (bookIdOrSlug: string) => void;
  fetchChapters: (bookIdOrSlug: string) => Promise<readonly Chapter[]>;
  fetchArticle: (idOrSlug: string) => Promise<Article>;
}

const LibraryContext = createContext<LibraryContextValue>(null!);

export function useLibrary(): LibraryContextValue {
  return useContext(LibraryContext);
}

function useLibraryProvider(deferInitialLoad: boolean): LibraryContextValue {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<LibraryStatus>("idle");
  const [books, setBooks] = useState<readonly Book[]>([]);
  const [locale, setLocale] = useState<Locale>("en");
  const [error, setError] = useState<Error | null>(null);
  const treeCache = useRef(new Map<string, BookTree>());

  const reload = useCallback(
    () =>
      api
        .listBooks(locale)
        .then(({ books }) => {
          setBooks(books);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [locale],
  );

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    if (!deferInitialLoad) {
      void reload();
      return;
    }

    const timeout = window.setTimeout(() => {
      void reload();
    }, 2000);
    return () => window.clearTimeout(timeout);
  }, [authStatus, deferInitialLoad, reload]);

  // Locale change invalidates tree cache — translated titles differ.
  useEffect(() => {
    treeCache.current.clear();
  }, [locale]);

  const fetchTree = useCallback(
    async (bookIdOrSlug: string) => {
      const key = `${locale}:${bookIdOrSlug}`;
      const cached = treeCache.current.get(key);
      if (cached) return cached;
      const tree = await api.getBookTree(bookIdOrSlug, locale);
      treeCache.current.set(key, tree);
      return tree;
    },
    [locale],
  );

  const invalidateTree = useCallback(
    (bookIdOrSlug: string) => {
      treeCache.current.delete(`${locale}:${bookIdOrSlug}`);
    },
    [locale],
  );

  const fetchChapters = useCallback(
    async (bookIdOrSlug: string) => {
      const { chapters } = await api.listChapters(bookIdOrSlug, locale);
      return chapters;
    },
    [locale],
  );

  const fetchArticle = useCallback(
    async (idOrSlug: string) => {
      const { article } = await api.getArticle(idOrSlug, locale);
      return article;
    },
    [locale],
  );

  return useMemo(
    () => ({
      status,
      books,
      locale,
      error,
      setLocale,
      reload,
      fetchTree,
      invalidateTree,
      fetchChapters,
      fetchArticle,
    }),
    [
      status,
      books,
      locale,
      error,
      reload,
      fetchTree,
      invalidateTree,
      fetchChapters,
      fetchArticle,
    ],
  );
}

export function LibraryProvider({
  children,
  deferInitialLoad = false,
}: {
  children: ReactNode;
  deferInitialLoad?: boolean;
}) {
  const value = useLibraryProvider(deferInitialLoad);
  return (
    <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>
  );
}
